import './style.css'
import * as THREE from 'three'
import { GUI } from 'dat.gui'
import Stats from 'three/examples/jsm/libs/stats.module'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { SVGRenderer } from "three/examples/jsm/renderers/SVGRenderer.js";
import sources from './sources';
import { Vector2 } from 'three'

/*
 * Stats
 */
// const stats = Stats()
// document.body.appendChild(stats.dom)

/*
 * Resources
 */
let textures = [];
let loaded = 0;
const toLoad = sources.length;
for (const source of sources) {
    if (source.type === "Texture") {
        var textureLoader = new THREE.TextureLoader();
        textureLoader.load(source.path, (texture) => {
            sourceLoaded(texture);
        })
    }
}
const sourceLoaded = (text) => {
    textures.push(text);

    loaded++;
    if (loaded === toLoad) {
        init();
    }
}

/*
 * INIT
 */ 
const init = () => {
    /**
     * Base
     */
    // Canvas
    const canvas = document.querySelector('canvas.webgl')

    // Scene
    const scene = new THREE.Scene()

    /**
     * Sizes
     */
    const sizes = {
        width: canvas.clientWidth,
        height: canvas.clientHeight
    }

    window.addEventListener('resize', () =>
    {
        // Update sizes
        sizes.width = canvas.clientWidth
        sizes.height = canvas.clientHeight

        // Update camera
        camera.aspect = sizes.width / sizes.height
        camera.updateProjectionMatrix()

        // Update renderer
        renderer.setSize(sizes.width, sizes.height)
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    })


    /**
     * Camera
     */
    // Base camera
    const camera = new THREE.PerspectiveCamera(75, sizes.width / sizes.height, 0.1, 100)
    camera.position.x = 0
    camera.position.y = 0
    camera.position.z = 1.8
    scene.add(camera)

    /**
     * Plane
     */
    const geometry = new THREE.PlaneGeometry( 1.5, 1, 500, 500 );
    let material = new THREE.MeshBasicMaterial( {
        transparent: true,
        map: textures[0]
    });

    const customUniforms = {
        uTime: {value: 0},
        uNumberSquares: {value: 30.0},
        uSpeed: {value: 0.1},
        uAmplitude: {value: 0.8},
        uPeriod: {value: 2.0}
    }

    material.onBeforeCompile = (shader) => {
        // Custom Uniforms
        shader.uniforms.uTime = customUniforms.uTime;
        shader.uniforms.uNumberSquares = customUniforms.uNumberSquares;
        shader.uniforms.uSpeed = customUniforms.uSpeed;
        shader.uniforms.uAmplitude = customUniforms.uAmplitude;
        shader.uniforms.uPeriod = customUniforms.uPeriod;

        // Vertex Shader
        shader.vertexShader = shader.vertexShader.replace(
            '#include <common>',
            `
                #include <common>

                // varying vec2 vUv;

                uniform float uTime;
                uniform float uSpeed;
                uniform float uAmplitude;
                uniform float uPeriod;

                vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }

                vec4 mod289(vec4 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
                
                vec4 permute(vec4 x) { return mod289(((x*34.0)+1.0)*x); }
                
                vec4 taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }
                
                vec3 fade(vec3 t) { return t*t*t*(t*(t*6.0-15.0)+10.0); }
                
                float cnoise(vec3 P) {
                    vec3 Pi0 = floor(P); 
                    vec3 Pi1 = Pi0 + vec3(1.0); 
                    Pi0 = mod289(Pi0);
                    Pi1 = mod289(Pi1);
                    vec3 Pf0 = fract(P); 
                    vec3 Pf1 = Pf0 - vec3(1.0); 
                    vec4 ix = vec4(Pi0.x, Pi1.x, Pi0.x, Pi1.x);
                    vec4 iy = vec4(Pi0.yy, Pi1.yy);
                    vec4 iz0 = Pi0.zzzz;
                    vec4 iz1 = Pi1.zzzz;
                    vec4 ixy = permute(permute(ix) + iy);
                    vec4 ixy0 = permute(ixy + iz0);
                    vec4 ixy1 = permute(ixy + iz1);
                    vec4 gx0 = ixy0 * (1.0 / 7.0);
                    vec4 gy0 = fract(floor(gx0) * (1.0 / 7.0)) - 0.5;
                    gx0 = fract(gx0);
                    vec4 gz0 = vec4(0.5) - abs(gx0) - abs(gy0);
                    vec4 sz0 = step(gz0, vec4(0.0));
                    gx0 -= sz0 * (step(0.0, gx0) - 0.5);
                    gy0 -= sz0 * (step(0.0, gy0) - 0.5);
                    vec4 gx1 = ixy1 * (1.0 / 7.0);
                    vec4 gy1 = fract(floor(gx1) * (1.0 / 7.0)) - 0.5;
                    gx1 = fract(gx1);
                    vec4 gz1 = vec4(0.5) - abs(gx1) - abs(gy1);
                    vec4 sz1 = step(gz1, vec4(0.0));
                    gx1 -= sz1 * (step(0.0, gx1) - 0.5);
                    gy1 -= sz1 * (step(0.0, gy1) - 0.5);
                    vec3 g000 = vec3(gx0.x,gy0.x,gz0.x);
                    vec3 g100 = vec3(gx0.y,gy0.y,gz0.y);
                    vec3 g010 = vec3(gx0.z,gy0.z,gz0.z);
                    vec3 g110 = vec3(gx0.w,gy0.w,gz0.w);
                    vec3 g001 = vec3(gx1.x,gy1.x,gz1.x);
                    vec3 g101 = vec3(gx1.y,gy1.y,gz1.y);
                    vec3 g011 = vec3(gx1.z,gy1.z,gz1.z);
                    vec3 g111 = vec3(gx1.w,gy1.w,gz1.w);
                    vec4 norm0 = taylorInvSqrt(vec4(dot(g000, g000), dot(g010, g010), dot(g100, g100), dot(g110, g110)));
                    g000 *= norm0.x;
                    g010 *= norm0.y;
                    g100 *= norm0.z;
                    g110 *= norm0.w;
                    vec4 norm1 = taylorInvSqrt(vec4(dot(g001, g001), dot(g011, g011), dot(g101, g101), dot(g111, g111)));
                    g001 *= norm1.x;
                    g011 *= norm1.y;
                    g101 *= norm1.z;
                    g111 *= norm1.w;
                    float n000 = dot(g000, Pf0);
                    float n100 = dot(g100, vec3(Pf1.x, Pf0.yz));
                    float n010 = dot(g010, vec3(Pf0.x, Pf1.y, Pf0.z));
                    float n110 = dot(g110, vec3(Pf1.xy, Pf0.z));
                    float n001 = dot(g001, vec3(Pf0.xy, Pf1.z));
                    float n101 = dot(g101, vec3(Pf1.x, Pf0.y, Pf1.z));
                    float n011 = dot(g011, vec3(Pf0.x, Pf1.yz));
                    float n111 = dot(g111, Pf1);
                    vec3 fade_xyz = fade(Pf0);
                    vec4 n_z = mix(vec4(n000, n100, n010, n110), vec4(n001, n101, n011, n111), fade_xyz.z);
                    vec2 n_yz = mix(n_z.xy, n_z.zw, fade_xyz.y);
                    float n_xyz = mix(n_yz.x, n_yz.y, fade_xyz.x);
                    return 2.2 * n_xyz;
                }
            `
        )
        shader.vertexShader = shader.vertexShader.replace(
            '#include <begin_vertex>',
            `
                float displacement = uAmplitude * cnoise( uPeriod * position + uTime * uSpeed );

                vec3 transformed = position + normal * displacement;

                vUv = uv;
            `
        )

        // Fragment Shader
        // shader.fragmentShader = shader.fragmentShader.replace(
        //     '#include <common>',
        //     `
        //         #include <common>
        //         uniform float uNumberSquares;
        //         varying vec2 vUv;
        //     `
        // )
        // shader.fragmentShader = shader.fragmentShader.replace(
        //     'gl_FragColor = vec4( outgoingLight, diffuseColor.a );',
        //     `
        //         float strength = step(0.9, mod(vUv.x * (1.0 * uNumberSquares), 1.0));


        //         strength += step(0.9, mod(vUv.y * (1.5 * uNumberSquares), 1.0));


        //         strength += step(0.497, max(abs(vUv.x - 0.5), abs(vUv.y - 0.5)));

        //         strength = 1.0 - strength;

        //         float alpha = 1.0 - step(0.5, strength);

        //         gl_FragColor = vec4(strength, strength, strength, alpha);
        //     `
        // )
    }

    const plane = new THREE.Mesh( geometry, material );
    scene.add(plane)

    /**
     * Renderer
     */
    const renderer = new THREE.WebGLRenderer({
        canvas: canvas,
        antialias: true,
        alpha: true,
        preserveDrawingBuffer: true
    })
    renderer.setSize(sizes.width, sizes.height)
    renderer.setClearColor( 0x000000, 0 ); // the default
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))

    /**
     * Animate
     */
    const clock = new THREE.Clock()
    let lastElapsedTime = 0

    const tick = () =>
    {
        const elapsedTime = clock.getElapsedTime()
        const deltaTime = elapsedTime - lastElapsedTime
        lastElapsedTime = elapsedTime

        customUniforms.uTime.value = elapsedTime

        // Update controls
        // controls.update()

        // Render
        renderer.render(scene, camera)

        // Update stats
        // stats.update()

        // Call tick again on the next frame
        window.requestAnimationFrame(tick)
    }

    tick()

    let btn = {
        screenshot: () => {
            console.log("take screenshot")
            takeScreenshot();
        }
    }

    /*
     * Dat Gui
     */
    const gui = new GUI()
    // gui.add(customUniforms.uNumberSquares, 'value', 10.0, 50.0).step(0.01).name('# Squares')
    gui.add(customUniforms.uSpeed, 'value', 0.0, 1.0).step(0.01).name('Speed')
    gui.add(customUniforms.uAmplitude, 'value', 0.0, 1.0).step(0.01).name('Amplitude')
    gui.add(customUniforms.uPeriod, 'value', 1.0, 4.0).step(0.01).name('Period')
    gui.add(btn, 'screenshot').name("Screenshot");


    const takeScreenshot = () => {    
        var renderer3D ,imgWidth ,imgHeight ;
        renderer3D = new THREE.WebGLRenderer({
            alpha: true,
            antialias: true,
            preserveDrawingBuffer: true
        });
        renderer3D.setSize(canvas.width*4, canvas.height*4);
        renderer3D.render(scene, camera);
        // download file like this.
        var a = document.createElement('a');
        a.href = renderer3D.domElement.toDataURL().replace("image/png", "image/octet-stream");
        a.download = 'tmrwfoundation-logo.png'
        a.click();
    }    
}